import { useNavigate } from 'react-router-dom';
import { arrangePlayLeagues, type ArrangedPlayLeague, type PlayLeagueDTO } from '@pickem/shared';
import { FocusShell } from '../components/Layout';
import { Spinner, ErrorBanner, EmptyState } from '../components/ui';
import { useApi } from '../lib/useApi';
import { useAuth } from '../lib/auth';

interface PlayData {
  leagues: PlayLeagueDTO[];
}

/**
 * Single-QR landing page: after login, players choose which league to play this
 * week via two big buttons. The button for the league they most recently played
 * is hoisted to the top and highlighted (see `arrangePlayLeagues`). Tapping a
 * league jumps into its current-week pick form (which auto-joins the player);
 * if that week has no open pick form yet, we fall back to the dashboard.
 */
export function LeaguePlayPage() {
  const { data, error, loading } = useApi<PlayData>('/play');
  const { switchLeague } = useAuth();
  const navigate = useNavigate();

  if (loading) return <FocusShell><Spinner /></FocusShell>;
  if (error) return <FocusShell><ErrorBanner message={error} /></FocusShell>;

  const leagues = data ? arrangePlayLeagues(data.leagues) : [];

  if (leagues.length === 0) {
    return (
      <FocusShell>
        <EmptyState title="No Leagues Yet">
          Pick&nbsp;’Em isn&apos;t open right now. Check back soon.
        </EmptyState>
      </FocusShell>
    );
  }

  async function choose(league: ArrangedPlayLeague) {
    if (league.pickPath) {
      navigate(league.pickPath);
      return;
    }
    // No open week / token yet — set the league where we can, then land on the
    // dashboard rather than dead-ending. switchLeague 403s for non-members, so
    // ignore failures and let the dashboard resolve context.
    try {
      await switchLeague(league.id);
    } catch {
      /* not a member yet — dashboard will show the join prompt */
    }
    navigate('/');
  }

  return (
    <FocusShell>
      <div className="text-center">
        <h1 className="font-display text-3xl uppercase text-ink">Choose Your League</h1>
        <p className="mt-2 text-sm text-muted">Tap where you&apos;re playing this week.</p>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        {leagues.map((league) => (
          <button
            key={league.slug}
            type="button"
            onClick={() => choose(league)}
            className={`flex flex-col items-center justify-center gap-2 rounded-lg px-6 py-9 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 focus-visible:ring-offset-cream ${
              league.highlighted
                ? 'bg-grove text-white shadow-card hover:bg-grove-dark'
                : 'card text-ink hover:bg-cream'
            }`}
          >
            {league.highlighted && (
              <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-widest">
                Your most recent league
              </span>
            )}
            <span className="font-display text-3xl uppercase">{league.name}</span>
          </button>
        ))}
      </div>
    </FocusShell>
  );
}
